import { MetricsKind } from "helpers/metrics/types";
import { ColumnVO } from "ag-grid-community/dist/lib/interfaces/iColumnVO";
import { AggFuncs, ValueTypes } from "constants/product-collection";
import { formatMessage } from "lib/globalize-util";
import { nullish } from "./nullish";

import { List, Map } from "immutable";
import { RollupSnapshotStatus } from "containers/folders/types/server-rollups";
import { STATUS_TO_RECREATE_SNAPSHOT } from "constants/server-rollup";

export const COL_DEF_SEPARATOR = "|";
export const ROLLUP_SIZE_PAGE = 500;
export const HASHTAGS_LIST_ITEMS_SIZE_PAGE = 5;
export const ASSORTMENT_NAME_DELIMITER = "::";
export const IMAGE_URL_FIELD_NAME = "image_url";
export const ASSORTMENT_FIELD_NAME = "assortment_name";
export const COLLECTION_ID_FIELD_NAME = "collection_id";
export const SERVER_SNAPSHOT_DATE_F0RMAT = "YYYY-MM-DD";
export const DEFAULT_DATE_VALUE = "0000-00-00";
export const AGGREGATE_COLUMN_PREFIX = "__agg_";
export const UNSET_GROUP_KEY = "_unset_group_key";
export const NU_CUSTOM_SORT_PREFIX = "nu_custom_sort_";
export const NU_COLLECTION_NAME_FIELD = "nu_collection_name_field";
export const HASHTAG_AUTO_COMPLETE_FIELD = "hashtag_auto_complete_field";

export interface IServerRollupResponse {
    items: IItemElement[];
    group_by: IGroupBy[];
    search_after: string[];
    door_total: IDoorTotal;
    meta: IMeta;
    size_set: string[];
    review_column_header: IReviewColumnHeader;
}

export interface IReviewColumnHeader {
    allocation_total: number;
    bulk_total: number;
    bulk_by_delivery: IAllocationByDelivery;
    allocation_by_delivery: IAllocationByDelivery;
    allocation_by_size: IAllocationByDelivery;
    allocation_by_size_delivery: IAllocationBySizeDelivery;
}

export interface IAllocationByDelivery {
    data: IAllocationByDeliveryData[];
}

export interface IAllocationByDeliveryData {
    aggregation: IAggregation;
}

export interface IAllocationBySizeDelivery {
    [size: string]: IAllocationByDelivery;
}

export interface IItemElement {
    item: IItem;
}

export interface IItem {
    id: string;
    fields: IField;
    bulks: IAllocation[];
    allocations: IAllocation[];
    collection_id: string;
    allocation_dimension_ids: string[];
    door_group_metrics: IDoorGroupMetric[];
    door_total_metrics: IDoorHeaderMetric[];
}

export type DateValueTpe = {
    day: number;
    month: number;
    year: number;
};

export type StringValue = { string_value: string };
export type NumberValue = { number_value: string };
export type BoolValue = { bool_value: string };
export type DateValue = { date_value: DateValueTpe };

export type FieldValueType = StringValue | NumberValue | BoolValue | DateValue;

export interface IField {
    [key: string]: FieldValueType;
}

export interface IAllocation {
    value_id: string[];
    quantity: string;
    cost: number;
    cost_with_conversion: number;
    retail: number;
    retail_with_conversion: number;
}

export interface IDoorTotal {
    doors: IDoorItem[];
}

export interface IDoorItem {
    delivery: string;
    door: string;
    units: number;
    skus: number;
    cost: number;
    cost_with_conversion: number;
    retail: number;
    retail_with_conversion: number;
    sku_total: number;
}

export interface IGroupBy {
    aggregation: IAggregation;
}

export interface IAggregation {
    group_key: FieldValueType;
    values: IAggregatedValues;
    item_count: number;

    group_metrics?: IDoorGroupMetric[];
    total_metrics?: IDoorHeaderMetric[];
}

export interface IAggregatedValues {
    total_quantity: { double_value: number };
    [key: string]: { double_value: number } | { string_value: string };
}

export interface IDoorGroupMetric {
    metric_kind: MetricsKind;
    delivery_id?: string;

    value?: number[]; // Only returned by be
}

export interface IDoorHeaderMetric {
    metric_kind: MetricsKind;
    delivery_id?: string;

    value?: number;
}

export interface IDoorGroupMetricName {
    name: string;
    doors?: string[];
}

export type IAttributeDoorsFilter = {
    [field: string]: string[];
};

export interface IDoorMetricsTotal {
    group_metrics: IDoorGroupMetric[];
    total_metrics: IDoorHeaderMetric[];
}

export interface IMeta {
    pivot_columns: any[];
    total: ITotal;
    door_metrics_total: IDoorMetricsTotal;
}

export interface ITotal {
    units: number;
    products: number;
    rows: number;
    costs: number;
    retail: number;
    sku_total: number;
}

interface IColumnDimensionValue {
    c: string[];
}
interface IRowDimensionValue {
    r: IColumnDimensionValue[];
}
export interface ISnapshotItem {
    fields: IField;
    bulks: IRowDimensionValue;
    allocations: IRowDimensionValue;
    id?: string;
    rowIndex?: number;
    selected_sizes?: string[];

    // Door Metrics:
    doorMetrics?: { [key: string]: number };
    totalMetrics?: { [key: string]: number };

    // calculated fields
    groupId?: string;
    startPath?: string[];
}

export enum AggFuncTypes {
    SUM = "SUM",
    COUNT = "COUNT",
    AVG = "AVG",
    SUM_WITH_PERCENTAGE = "SUM_WITH_PERCENTAGE",
    COUNT_WITH_PERCENTAGE = "COUNT_WITH_PERCENTAGE",
}

export const AggregationFuncGridToServerMap = {
    [AggFuncs.SUM.toUpperCase()]: AggFuncTypes.SUM,
    [AggFuncs.COUNT.toUpperCase()]: AggFuncTypes.COUNT,
    [AggFuncs.AVG.toUpperCase()]: AggFuncTypes.AVG,
    [AggFuncs.SUM_WITH_PERCENT.toUpperCase()]: AggFuncTypes.SUM_WITH_PERCENTAGE,
    [AggFuncs.COUNT_WITH_PERCENT.toUpperCase()]: AggFuncTypes.COUNT_WITH_PERCENTAGE,
};

const DOOR_GROUP_AND_SELECTIONS_SEPARATOR = "===";
export const DOOR_METRICS_GROUP_NAME_SEPARATOR = "|";
export const DOOR_DIMENSION_METRICS_GROUP_NAME = "DOOR";

export const buildDoorGroupNameSelected = (doorGroup: string, classification: string) =>
    `${doorGroup}${DOOR_GROUP_AND_SELECTIONS_SEPARATOR}${classification}`;

export const buildServerRollupRowNodeId = (itemId: string, collectionId: string) => `${itemId}#${collectionId}`;

export const shouldRecreateSnapshot = (status: RollupSnapshotStatus) => STATUS_TO_RECREATE_SNAPSHOT.includes(status);

export const getBlankTextValue = (useNull?: boolean) => (useNull ? null : formatMessage("(Blanks)")); // is we pass null aggrid uses the blank text and this works with all types //formatMessage("(Blanks)");

export const isFieldWithCustomSorting = (schemaField: Map<string, any>) =>
    schemaField?.getIn(["constraint", "allowed_values"])?.size > 0;

export const getFieldNamesFromValueType = (
    itemDisplayProperty: List<Map<string, any>>,
    valueType: ValueTypes,
): string[] => {
    if (!itemDisplayProperty) {
        return [];
    }
    const schemas = itemDisplayProperty.filter((schemaKey) => schemaKey.get("value_type") === valueType);
    return schemas.size ? schemas.map((schema) => schema.getIn(["field_name"])).toArray() : [];
};

export const parseStringIntoDate = (value: string): DateValueTpe => {
    if (!value || value === DEFAULT_DATE_VALUE) {
        return null;
    }

    if (value === UNSET_GROUP_KEY) {
        return value as any;
    }

    const [year, month, day] = value.split("-");
    return {
        day: +day,
        month: +month,
        year: +year,
    };
};

export const mapDateFields = (
    dateFields: string[],
    data: IField,
    valueCols: ColumnVO[] = [],
): { [key: string]: DateValue } =>
    dateFields.reduce((acc, key) => {
        const isAggregated = valueCols?.some((it) => it.field === key);
        const value = !isAggregated && parseStringIntoDate((data[key] as StringValue)?.string_value);
        if (value) {
            acc[key] = { date_value: value };
        }

        return acc;
    }, {});

export const mapCollectionFieldValue = (
    valueType: ValueTypes,
    value: any,
    dateAsString: boolean = false,
): FieldValueType => {
    if (value === "" && dateAsString) {
        return { string_value: UNSET_GROUP_KEY };
    }

    if (value === UNSET_GROUP_KEY) {
        return { string_value: value };
    }

    switch (valueType) {
        case ValueTypes.WITH_PERCENT:
            return {
                number_value: `${nullish(value) ? "" : value}`,
            };
        case ValueTypes.BOOL:
            return {
                bool_value: value,
            };
        case ValueTypes.NUMBER:
            return {
                number_value: value,
            };
        case ValueTypes.DATE:
        case ValueTypes.STRING:
        default: {
            if (!dateAsString && valueType === ValueTypes.DATE) {
                return { date_value: value };
            }
            return {
                string_value: `${nullish(value) ? "" : value}`,
            };
        }
    }
};

export const createColsHash = (colDefs) => {
    if (!colDefs) {
        return null;
    }
    const parts = [];
    colDefs.forEach((colDef) => {
        if (colDef.children) {
            parts.push(colDef.groupId);
            parts.push("[" + createColsHash(colDef.children) + "]");
        } else {
            parts.push(colDef.colId);
            // headerName can change if the aggFunc was changed in a value col. if we didn't
            // do this, then the grid would not pick up on new header names as we move from
            // eg min to max.
            if (colDef.headerName) {
                parts.push(colDef.headerName);
            }
        }
    });
    return parts.join(",");
};

export const readDoorMetrics = (
    doorMetricsTotal: IDoorMetricsTotal,
    doorGroups: IDoorGroupMetricName[],
    hasDeliveries: boolean,
) => {
    const doorMetrics = doorMetricsTotal?.group_metrics?.reduce((acc, groupMetric) => {
        const doorGroupFields = (doorGroups || []).reduce((iacc, dg, index) => {
            iacc[dg.name] = groupMetric.value[index];
            return iacc;
        }, {});

        acc[groupMetric.metric_kind] = {
            ...(acc[groupMetric.metric_kind] || {}),
            ...(hasDeliveries ? { [groupMetric.delivery_id]: doorGroupFields } : doorGroupFields),
        };

        return acc;
    }, {});

    const totalMetrics = doorMetricsTotal?.total_metrics?.reduce((acc, totalMetric) => {
        acc[totalMetric.metric_kind] = hasDeliveries
            ? {
                  ...(acc[totalMetric.metric_kind] || {}),
                  [totalMetric.delivery_id]: totalMetric.value,
              }
            : totalMetric.value;

        return acc;
    }, {});

    return {
        doorMetrics,
        totalMetrics,
    };
};

export const readRows = (
    response: IServerRollupResponse,
    startRow: number,
    collectionsRefs,
    itemDisplayProperty: List<Map<string, any>>,
    addCalculatedFields?: () => {},
    valueCols?: ColumnVO[],
    doorGroups?: IDoorGroupMetricName[],
    hasDeliveries: boolean = false,
): ISnapshotItem[] => {
    const dateFields = getFieldNamesFromValueType(itemDisplayProperty, ValueTypes.DATE);

    return response.items.map(({ item }, rowIndex) => {
        const { allocations, bulks, door_group_metrics, door_total_metrics, ...restItem } = item;
        return {
            ...restItem,
            ...(addCalculatedFields?.() || {}),
            ...readDoorMetrics(
                {
                    group_metrics: door_group_metrics,
                    total_metrics: door_total_metrics,
                },
                doorGroups,
                hasDeliveries,
            ),
            fields: {
                ...restItem.fields,
                ...mapDateFields(dateFields, restItem.fields, valueCols),
                [ASSORTMENT_FIELD_NAME]: {
                    string_value: `${collectionsRefs.get(item.collection_id)}${ASSORTMENT_NAME_DELIMITER}${
                        item.collection_id
                    }`,
                },
            },
            allocations: {
                r: allocations.map((it) => {
                    return {
                        c: it.value_id.concat([it.quantity]),
                    };
                }),
            },
            bulks: {
                r: bulks.map((it) => {
                    return {
                        c: it.value_id.concat([it.quantity]),
                    };
                }),
            },
            rowIndex: startRow + rowIndex,
        };
    });
};
